"use client"

import * as React from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Search, X, RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { GameSelector } from "@/components/search/game-selector"
import { DaySelector } from "@/components/search/day-selector"
import { RegionSelector } from "@/components/search/region-selector"

interface LeagueFiltersProps {
  onFiltersChange: (filters: {
    games: string[]
    days: string[]
    regions: string[]
  }) => void
}

export function LeagueFilters({ onFiltersChange }: LeagueFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [selectedGames, setSelectedGames] = React.useState<string[]>(() => {
    const games = searchParams.get("games")
    return games ? games.split(",") : []
  })
  const [selectedDays, setSelectedDays] = React.useState<string[]>(() => {
    const days = searchParams.get("days")
    return days ? days.split(",") : []
  })
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>(() => {
    const regions = searchParams.get("regions")
    return regions ? regions.split(",") : []
  })

  // Update filters when selections change
  React.useEffect(() => {
    onFiltersChange({
      games: selectedGames,
      days: selectedDays,
      regions: selectedRegions,
    })

    // Update URL
    const params = new URLSearchParams()
    if (selectedGames.length > 0) params.set("games", selectedGames.join(","))
    if (selectedDays.length > 0) params.set("days", selectedDays.join(","))
    if (selectedRegions.length > 0) params.set("regions", selectedRegions.join(","))
    
    const newUrl = params.toString() ? `/leagues?${params.toString()}` : "/leagues"
    router.replace(newUrl, { scroll: false })
  }, [selectedGames, selectedDays, selectedRegions, onFiltersChange, router])

  const hasFilters = selectedGames.length > 0 || selectedDays.length > 0 || selectedRegions.length > 0

  const clearFilters = () => {
    setSelectedGames([])
    setSelectedDays([])
    setSelectedRegions([])
  }

  const activeFilterCount = selectedGames.length + selectedDays.length + selectedRegions.length

  return (
    <div className="bg-card/80 backdrop-blur-md border border-border rounded-xl p-4 md:p-6 sticky top-20 z-30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Search className="h-5 w-5 text-primary" />
          <h2 className="font-semibold text-foreground">Find Leagues</h2>
          {activeFilterCount > 0 && (
            <span className="bg-primary/20 text-primary text-xs font-medium px-2 py-0.5 rounded-full">
              {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-muted-foreground hover:text-foreground gap-1.5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
            Game / League Type
          </label>
          <GameSelector 
            selectedGames={selectedGames} 
            onSelectionChange={setSelectedGames} 
          />
        </div>
        
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
            Day of Week
          </label>
          <DaySelector 
            selectedDays={selectedDays} 
            onSelectionChange={setSelectedDays} 
          />
        </div>
        
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
            Region
          </label>
          <RegionSelector 
            selectedRegions={selectedRegions} 
            onSelectionChange={setSelectedRegions} 
          />
        </div>
      </div>

      {/* Active Filter Pills */}
      {hasFilters && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border/50">
          {selectedGames.map(game => (
            <button
              key={game}
              onClick={() => setSelectedGames(prev => prev.filter(g => g !== game))}
              className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-sm px-3 py-1 rounded-full hover:bg-primary/20 transition-colors"
            >
              {game.replace(/-/g, " ")}
              <X className="h-3 w-3" />
            </button>
          ))}
          {selectedDays.map(day => (
            <button
              key={day}
              onClick={() => setSelectedDays(prev => prev.filter(d => d !== day))}
              className="inline-flex items-center gap-1.5 bg-accent/10 text-accent text-sm px-3 py-1 rounded-full hover:bg-accent/20 transition-colors"
            >
              {day}
              <X className="h-3 w-3" />
            </button>
          ))}
          {selectedRegions.map(region => (
            <button
              key={region}
              onClick={() => setSelectedRegions(prev => prev.filter(r => r !== region))}
              className="inline-flex items-center gap-1.5 bg-secondary text-foreground text-sm px-3 py-1 rounded-full hover:bg-secondary/80 transition-colors"
            >
              {region.replace(/-/g, " ")}
              <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
